// The panel's information architecture — ONE source of truth.
//
// The sidebar, the ⌘K search and every page header read from this file, so a
// screen can never be called one thing in the menu and another in its title,
// and a new page cannot be added to the app without saying what it is FOR.
//
// The order below is the order the business actually works:
//   Today (what needs me now) → the order flow (money in) → stock & buying
//   (money out) → people → reports → audit → setup.
//
// `badge` names a queue from GET /admin/worklist/. Whatever is waiting there
// shows as a live count next to the item, so nobody has to open a page to
// discover there is work on it.

import {
  FiSun,
  FiShoppingCart,
  FiCreditCard,
  FiTruck,
  FiRotateCcw,
  FiBox,
  FiLayers,
  FiDollarSign,
  FiSlash,
  FiRepeat,
  FiRefreshCw,
  FiUsers,
  FiBriefcase,
  FiUserCheck,
  FiClipboard,
  FiTrendingUp,
  FiPieChart,
  FiBarChart2,
  FiEdit3,
  FiTrash2,
  FiFileText,
  FiSettings,
  FiKey,
  FiUserPlus,
} from 'react-icons/fi';
import { ROUTES } from './app.constants';

export const NAV_SECTIONS = [
  {
    id: 'home',
    label: 'START HERE',
    items: [
      {
        label: 'Today',
        path: ROUTES.TODAY,
        icon: FiSun,
        purpose: 'Everything waiting for you right now, in the order to do it.',
      },
    ],
  },
  {
    id: 'flow',
    label: 'THE ORDER FLOW',
    caption: 'money coming in — follow it top to bottom',
    items: [
      {
        label: 'Orders',
        path: ROUTES.ORDERS,
        icon: FiShoppingCart,
        step: 1,
        purpose: 'Every order the sales team closed, grouped by customer.',
      },
      {
        label: 'Payments',
        path: ROUTES.PAYMENTS,
        icon: FiCreditCard,
        step: 2,
        badge: 'confirm_payment',
        purpose: 'Verify the money the customer paid — sales deals AND the counter book — and chase what is still owed.',
      },
      {
        label: 'Dispatch',
        path: ROUTES.DISPATCH,
        icon: FiTruck,
        step: 3,
        badge: 'dispatch',
        purpose: 'Hand over paid orders. This is what completes a sale.',
      },
      {
        label: 'Returns & Refunds',
        path: ROUTES.RETURNS,
        icon: FiRotateCcw,
        step: 4,
        badge: 'pay_refund',
        purpose: 'Parts that came back, and the cash owed to those customers.',
      },
    ],
  },
  {
    id: 'cars',
    label: 'CARS',
    caption: 'whole cars — ours, and other owners on commission',
    items: [
      {
        label: 'Cars',
        path: ROUTES.CARS,
        icon: FiKey,
        badge: 'car_owner_payout',
        purpose: "Every car on the lot — what it cost, what it is asking, and what CAP earns when it goes.",
      },
      {
        label: 'Cars Team',
        path: ROUTES.CARS_STAFF,
        icon: FiUserPlus,
        purpose: 'The car team accounts — they log in on their own app to work the lot.',
      },
    ],
  },
  {
    id: 'stock',
    label: 'STOCK & BUYING',
    caption: 'what we own and what we owe',
    items: [
      {
        label: 'Stock Cars',
        path: ROUTES.PRODUCTS,
        icon: FiBox,
        purpose: 'Donor cars on the yard — what each cost and how much is recovered.',
      },
      {
        label: 'Outside Parts',
        path: ROUTES.OUTSIDE_PARTS,
        icon: FiLayers,
        purpose: 'Parts bought in bulk and held on the shelf, matched to inquiries automatically.',
      },
      {
        label: 'OSM Purchases',
        path: ROUTES.OSM,
        icon: FiDollarSign,
        badge: 'vendor_dues',
        purpose: 'Outside-market buys — what the vendor charged and what is still unpaid.',
      },
      {
        label: 'Restock Requests',
        path: ROUTES.NOT_AVAILABLE,
        icon: FiSlash,
        badge: 'restock',
        purpose: 'Parts customers are waiting for. Restock one and they get called back.',
      },
      {
        label: 'Trade-ins',
        path: ROUTES.TRADE_INS,
        icon: FiRepeat,
        purpose: 'Old parts taken in on an exchange — resell, dismantle or scrap them.',
      },
      {
        label: 'Exchange Parts',
        path: ROUTES.EXCHANGE_PARTS,
        icon: FiRefreshCw,
        purpose: 'Which part types can be sold with an old-part trade-in — one list drives supervisor quotes and the counter forms.',
      },
    ],
  },
  {
    id: 'people',
    label: 'PEOPLE',
    items: [
      {
        label: 'Customers',
        path: ROUTES.CUSTOMERS,
        icon: FiUsers,
        purpose: 'Every customer, their orders and their history.',
      },
      {
        label: 'Salespersons',
        path: ROUTES.SALESPERSONS,
        icon: FiBriefcase,
        purpose: 'The sales team — what each one sold and collected.',
      },
      {
        label: 'Supervisors',
        path: ROUTES.SUPERVISORS,
        icon: FiUserCheck,
        purpose: 'Supervisor accounts and access.',
      },
      {
        label: 'Sale Notes',
        path: ROUTES.SALE_REMINDERS,
        icon: FiClipboard,
        purpose: 'Every direct sale — supervisor notes and the counter book, editable.',
      },
    ],
  },
  {
    id: 'money',
    label: 'MONEY & REPORTS',
    items: [
      {
        label: 'Money Out',
        path: ROUTES.MONEY_INVESTMENTS,
        icon: FiTrendingUp,
        purpose: 'Chits, borrowings, salary and rent — every rupee committed to go out.',
      },
      {
        label: 'Expense Report',
        path: ROUTES.EXPENSE_REPORT,
        icon: FiPieChart,
        purpose: 'What was spent, by category and by day, week, month or year.',
      },
      {
        label: 'Business Overview',
        path: ROUTES.DASHBOARD,
        icon: FiBarChart2,
        purpose: 'The whole business in charts — sales vs expenses, sources, recovery.',
      },
      {
        label: 'Sales Analytics',
        path: ROUTES.ANALYTICS,
        icon: FiTrendingUp,
        purpose: 'The inquiry funnel and how the team converts it.',
      },
    ],
  },
  {
    id: 'audit',
    label: 'RECORDS',
    caption: 'read-only — nothing here can be edited',
    items: [
      {
        label: 'Price Changes',
        path: ROUTES.PRICE_CHANGES,
        icon: FiEdit3,
        purpose: 'Every price the admin corrected at the checkpoint, and the reason given.',
      },
      {
        label: 'Deleted Records',
        path: ROUTES.DELETED_RECORDS,
        icon: FiTrash2,
        purpose: 'Everything removed from the panel, kept as proof of what was there.',
      },
    ],
  },
  {
    id: 'setup',
    label: 'SETUP',
    items: [
      {
        label: 'Templates',
        path: ROUTES.TEMPLATES,
        icon: FiFileText,
        purpose: 'The WhatsApp messages the team sends customers.',
      },
      {
        label: 'Settings',
        path: ROUTES.SETTINGS,
        icon: FiSettings,
        purpose: 'Panel configuration, your own password, and the Play Store update notice for the supervisor app.',
      },
    ],
  },
];

/** Flat list of every destination — for ⌘K search and page-header lookup. */
export const NAV_ITEMS = NAV_SECTIONS.flatMap((s) =>
  s.items.map((i) => ({ ...i, section: s.label })),
);

/** The nav entry for a path, so a page can title itself from the menu. */
export const navItemFor = (pathname) =>
  NAV_ITEMS.find((i) => i.path === pathname)
  || NAV_ITEMS.find((i) => i.path !== '/' && pathname.startsWith(`${i.path}/`));

// ─── The work queues ──────────────────────────────────────────────────────────
// One entry per queue returned by /admin/worklist/. `tone` drives the colour:
// act = money is stuck until you do this, chase = someone owes us, pay = we owe
// someone, info = worth knowing.
export const WORK_QUEUES = [
  {
    key: 'confirm_payment',
    title: 'Verify payments',
    body: 'Sales says these customers paid. Until you confirm it, the order cannot be dispatched.',
    action: 'Verify now',
    path: ROUTES.PAYMENTS,
    icon: FiCreditCard,
    tone: 'act',
    money: 'order value',
  },
  {
    key: 'dispatch',
    title: 'Hand over paid orders',
    body: 'These are paid for and still sitting with us. The customer is waiting.',
    action: 'Open dispatch',
    path: ROUTES.DISPATCH,
    icon: FiTruck,
    tone: 'act',
    money: 'order value',
  },
  {
    key: 'pay_refund',
    title: 'Pay refunds',
    body: 'Cash we owe back to customers on returned parts.',
    action: 'Pay out',
    path: ROUTES.RETURNS,
    icon: FiRotateCcw,
    tone: 'pay',
    money: 'to hand back',
  },
  {
    key: 'collect_balance',
    title: 'Collect balances',
    body: 'Advance deals where the customer still owes the rest.',
    action: 'See who owes',
    path: ROUTES.PAYMENTS,
    icon: FiDollarSign,
    tone: 'chase',
    money: 'still to collect',
  },
  {
    key: 'vendor_price',
    title: 'Enter vendor cost',
    body: 'Parts bought from the outside market with no purchase price recorded — profit is unknown until you enter it.',
    action: 'Enter cost',
    path: ROUTES.OSM,
    icon: FiEdit3,
    tone: 'info',
  },
  {
    key: 'vendor_dues',
    title: 'Pay vendors',
    body: 'Outside-market vendors still owed money on parts we already sold.',
    action: 'Settle dues',
    path: ROUTES.OSM,
    icon: FiDollarSign,
    tone: 'pay',
    money: 'owed to vendors',
  },
  {
    key: 'restock',
    title: 'Restock requests',
    body: 'Customers are waiting for these parts. Restock one and the sales team calls them back.',
    action: 'See demand',
    path: ROUTES.NOT_AVAILABLE,
    icon: FiSlash,
    tone: 'info',
  },
  {
    key: 'car_owner_payout',
    title: "Pay car owners",
    body: "Commission cars that have sold. The owner's share of the money is still sitting with us.",
    action: 'Settle owners',
    path: ROUTES.CARS,
    icon: FiKey,
    tone: 'pay',
    money: 'owed to owners',
  },
  {
    key: 'car_balance',
    title: 'Collect car balances',
    body: 'Buyers who paid a token on a car and still owe the rest.',
    action: 'See who owes',
    path: ROUTES.CARS,
    icon: FiKey,
    tone: 'chase',
    money: 'still to collect',
  },
];
